import {
  EnvelopeIcon,
  PhoneIcon,
  WarningIcon,
  CopyIcon,
  CalendarBlankIcon,
  LockSimpleIcon,
  SlidersIcon,
  ArrowUpRightIcon,
  UserIcon,
  PencilSimpleIcon,
  ArrowUUpLeftIcon,
  WalletIcon,
  ReceiptIcon,
  CircleDashedIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { ArCollectionAction } from '@erp/rpc';

/**
 * Shared icon + label maps for the ArCollectionAction taxonomy. Used by the
 * customer-intelligence panel and the short-pay recommendation card so the two
 * surfaces stay visually consistent and the maps stay exhaustive (the
 * Record<ArCollectionAction, …> type fails the build if an action is missed).
 */
export const AR_COLLECTION_ACTION_ICONS: Record<ArCollectionAction, Icon> = {
  send_payment_reminder: EnvelopeIcon,
  schedule_call: PhoneIcon,
  send_final_notice: WarningIcon,
  send_invoice_copy: CopyIcon,
  offer_payment_plan: CalendarBlankIcon,
  place_on_credit_hold: LockSimpleIcon,
  adjust_credit_limit: SlidersIcon,
  escalate_to_manager: ArrowUpRightIcon,
  email_sales_person: UserIcon,
  adjust_invoice: PencilSimpleIcon,
  refund_overpayment: ArrowUUpLeftIcon,
  apply_credit_to_account: WalletIcon,
  issue_credit_memo: ReceiptIcon,
};

export const AR_COLLECTION_ACTION_LABELS: Record<ArCollectionAction, string> = {
  send_payment_reminder: 'Send Payment Reminder',
  schedule_call: 'Schedule Call',
  send_final_notice: 'Send Final Notice',
  send_invoice_copy: 'Send Invoice Copy',
  offer_payment_plan: 'Offer Payment Plan',
  place_on_credit_hold: 'Place on Credit Hold',
  adjust_credit_limit: 'Adjust Credit Limit',
  escalate_to_manager: 'Escalate to Manager',
  email_sales_person: 'Email Sales Person',
  adjust_invoice: 'Adjust Invoice',
  refund_overpayment: 'Refund Overpayment',
  apply_credit_to_account: 'Apply Credit to Account',
  issue_credit_memo: 'Issue Credit Memo',
};

/**
 * Actions that result in an outbound email and so can seed the collection-email
 * draft dialog. `email_sales_person` is included because we can now resolve a
 * salesperson email server-side (Famous ingest pipes AR_CUST.SALESPERSONNAMEIDX
 * and the load-level override into `Invoice.salesPersonContactId`); the
 * recommendation card seeds the TO field with that address when the user
 * drafts from this action. The other actions (credit hold, credit-limit
 * adjustment, schedule call, escalate, adjust invoice) are either internal
 * operations or non-email touchpoints, so we don't offer a "Draft email"
 * button for them.
 */
const EMAILABLE_ACTIONS = new Set<ArCollectionAction>([
  'send_payment_reminder',
  'send_final_notice',
  'send_invoice_copy',
  'offer_payment_plan',
  'email_sales_person',
]);

export function isEmailableArCollectionAction(
  action: ArCollectionAction,
): boolean {
  return EMAILABLE_ACTIONS.has(action);
}

// ---------------------------------------------------------------------------
// String-id-safe accessors. Action ids are now org/customer-configurable free
// strings (not the locked enum), so a recommendation's `action` may be a custom
// id with no entry in the maps above. The recommendation card prefers the
// server-provided `actionCatalog` (label + emailable) and uses these helpers as
// the fallback for a known-default id or an unknown custom id. The static maps
// stay authoritative for the enum-based customer-intelligence panel.
// ---------------------------------------------------------------------------

/** Icon for any action id: the known-default icon, or a neutral fallback. */
export function getArCollectionActionIcon(actionId: string): Icon {
  return (
    (AR_COLLECTION_ACTION_ICONS as Record<string, Icon>)[actionId] ??
    CircleDashedIcon
  );
}

/** Turn a snake_case id into a readable label, e.g. "chase_ap" → "Chase Ap". */
export function humanizeActionId(actionId: string): string {
  return actionId
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Display label for an action id: prefer the catalog label the server resolved
 * from config, then the known-default label, then a humanized id.
 */
export function getArCollectionActionLabel(
  actionId: string,
  catalogLabel?: string,
): string {
  return (
    catalogLabel ??
    (AR_COLLECTION_ACTION_LABELS as Record<string, string>)[actionId] ??
    humanizeActionId(actionId)
  );
}

/**
 * Whether an action id should offer a "Draft email" button: prefer the catalog
 * flag the server resolved from config, else the known-default set.
 */
export function isEmailableActionId(
  actionId: string,
  catalogEmailable?: boolean,
): boolean {
  return (
    catalogEmailable ?? EMAILABLE_ACTIONS.has(actionId as ArCollectionAction)
  );
}
